import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  ApiRequestStatus,
  FileDownloaded,
  Document,
  DocumentsAreaSections,
  DocumentType,
} from '../../model';
import { GlobalActions } from '../../model/global-actions';
import {
  documentsAreaCommonSliceActions,
  LoginState,
  messagesForDownloadDocumentsFileError,
  RootState,
  selectDocumentsAreaCommonState,
} from '../../store';
import {
  downloadDocumentsFile,
  markDocumentAsRead,
} from '../../store/features/documents/common/common-thunks';
import { useAppDispatch, useAppSelector } from '../redux';

export interface DownloadsDownloadFileLogic {
  fileDownloaded: FileDownloaded;
  fileDownloadedRequestStatus: ApiRequestStatus;
  onDownloadFile: (
    document: Document,
    documentsAreaSection: DocumentsAreaSections,
    documentType?: DocumentType
  ) => void;
}

export const useDocumentsDownloadFileLogic = (
  globalActions: GlobalActions
): DownloadsDownloadFileLogic => {
  const { formatMessage } = useIntl();
  const { showToast, closeToast } = globalActions;
  const dispatch = useAppDispatch();
  const [documentsAreaSection, setDocumentsAreaSection] =
    useState<DocumentsAreaSections>();
  const [document, setDocument] = useState<Document>();
  const [documentType, setDocumentType] = useState<DocumentType>();

  const { fileDownloaded, fileDownloadedRequestStatus, fileDownloadedError } =
    useAppSelector(selectDocumentsAreaCommonState);

  const { tokenProspect } = useAppSelector(
    (state: RootState): LoginState => state.features.login
  );

  const informativeMode: boolean = useAppSelector(
    (state: RootState) => state.data.user.informativeMode
  );

  const [downloadError, setDownloadError] = useState(0);

  const onDownloadFile = useCallback(
    (
      doc: Document,
      docAreaSection: DocumentsAreaSections,
      docType?: DocumentType
    ) => {
      setDocumentsAreaSection(docAreaSection);
      setDocument(doc);
      if (docType) {
        setDocumentType(docType);
      }

      dispatch(downloadDocumentsFile({ document: doc }))
        .unwrap()
        .catch((_e1) => {
          setDownloadError(1);
          if (tokenProspect)
            dispatch(
              downloadDocumentsFile({ document: doc, token: tokenProspect })
            )
              .unwrap()
              .catch((_e2) => {
                setDownloadError(2);
              });
        });
    },
    [dispatch, tokenProspect]
  );

  useEffect(() => {
    const inError = tokenProspect ? downloadError == 2 : downloadError == 1;
    if (inError && fileDownloadedError) {
      const errorMessages =
        messagesForDownloadDocumentsFileError(fileDownloadedError);
      showToast({
        type: 'error',
        title: formatMessage(errorMessages.title),
        description:
          errorMessages.description && formatMessage(errorMessages.description),
        close: closeToast,
      });
      dispatch(documentsAreaCommonSliceActions.clearDownloadFile());
    }
  }, [dispatch, fileDownloadedError, closeToast, formatMessage, showToast]);

  useEffect(() => {
    if (fileDownloadedRequestStatus === 'succeeded') {
      // The documents downloaded by an informative user are not marked as read
      if (document && !document.flagIsRead && !informativeMode) {
        dispatch(
          markDocumentAsRead({
            document,
            documentsAreaSection,
            documentType,
          })
        );
      }
    }
  }, [
    dispatch,
    fileDownloadedRequestStatus,
    document,
    documentsAreaSection,
    documentType,
    informativeMode,
  ]);

  return {
    fileDownloaded,
    fileDownloadedRequestStatus,

    onDownloadFile,
  };
};
